<template>
  <!-- FAB toggle button -->
  <button
    v-show="aiEnabled && (fabVisible || balloonActive || isOpen)"
    class="cw-fab"
    :class="{ 'cw-fab--open': isOpen, 'cw-fab--hint': balloonActive }"
    :style="fabStyle"
    @click="toggle"
    title="Beto - Assistente Digital"
  >
    <svg v-if="!isOpen" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
      <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/>
    </svg>
    <svg v-else viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
      <line x1="18" y1="6" x2="6" y2="18"/>
      <line x1="6" y1="6" x2="18" y2="18"/>
    </svg>
    <span v-if="unread > 0 && !isOpen" class="cw-fab-badge">{{ unread }}</span>
  </button>

  <!-- Balão de sugestão — conversa da Empresa Cliente em atendimento; texto muda pro nome da
       empresa assim que a consulta automática de CNPJ resolve (ver nameHintActive) -->
  <Transition name="cw-balloon">
    <div v-if="balloonActive && !isOpen" class="cw-hint-balloon" :style="fabStyle" @click="toggle">
      {{ balloonText }}
    </div>
  </Transition>

  <!-- Chat panel -->
  <Transition name="cw">
    <div v-if="isOpen" class="cw-panel" :style="panelStyle">

      <!-- Resize handles -->
      <div class="cw-rz cw-rz-top"    @mousedown.prevent="startResize($event, 'top')"></div>
      <div class="cw-rz cw-rz-right"  @mousedown.prevent="startResize($event, 'right')"></div>
      <div class="cw-rz cw-rz-corner" @mousedown.prevent="startResize($event, 'corner')">
        <svg viewBox="0 0 10 10"><path d="M9 1 L1 9 M9 5 L5 9 M9 9 L9 9" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/></svg>
      </div>

      <!-- Header -->
      <div class="cw-header">
        <div class="cw-hleft">
          <img :src="logoUrl" alt="ISBET" class="cw-logo-img" />
          <div class="cw-vline"></div>
          <div>
            <div class="cw-htitle">Beto - Assistente Digital</div>
            <div class="cw-hsub">Jovem Aprendiz &amp; Estágio</div>
          </div>
        </div>
        <div class="cw-hstatus"><span class="cw-dot"></span>Online</div>
      </div>
      <div class="cw-stripe"></div>

      <!-- Quick chips (só fora do fluxo 2 — lá quem manda são as ações de cliente, abaixo) -->
      <div v-if="messages.length === 0 && !isEmpresaCliente" class="cw-chips">
        <button v-for="c in CHIPS" :key="c.q" class="cw-chip" @click="quickSend(c.q)">{{ c.label }}</button>
      </div>

      <!-- Ações do fluxo 2 (Empresa Cliente) — fixas no topo, antes dos detalhes da empresa -->
      <div v-if="isEmpresaCliente && clienteActions.length" class="cw-qgrid cw-qgrid--top">
        <button v-for="a in clienteActions" :key="a.label" class="cw-qbtn" @click="quickSend(a.prompt)">
          <span class="cw-qi">{{ a.icon }}</span>{{ a.label }}
        </button>
      </div>

      <!-- Messages -->
      <div class="cw-msgs" ref="msgsEl">
        <div v-if="messages.length === 0" class="cw-welcome">
          <div class="cw-w-icon">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round">
              <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/>
            </svg>
          </div>
          <h3>Olá! Como posso ajudar?</h3>
          <p>Sou o assistente do ISBET — cadastros, direitos trabalhistas, relatórios e muito mais.</p>
          <div v-if="!isEmpresaCliente" class="cw-qgrid">
            <button class="cw-qbtn" @click="quickSend('Quero cadastrar um novo aprendiz')">
              <span class="cw-qi">📤</span>Novo aprendiz
            </button>
            <button class="cw-qbtn" @click="quickSend('Quais são os direitos do jovem aprendiz pela CLT?')">
              <span class="cw-qi">⚖️</span>Direitos CLT
            </button>
            <button class="cw-qbtn" @click="quickSend('Quais unidades do ISBET existem em SP?')">
              <span class="cw-qi">📍</span>Unidades ISBET
            </button>
            <button class="cw-qbtn" @click="quickSend('Como funciona o programa de aprendizagem do ISBET?')">
              <span class="cw-qi">📋</span>Programa ISBET
            </button>
          </div>
        </div>

        <div v-for="(m, i) in messages" :key="i" class="cw-row" :class="m.role">
          <div class="cw-av" :class="m.role"
            :style="m.role === 'user' ? { background: userAvatarBg } : {}">
            <img v-if="m.role === 'bot'" :src="logoUrl" alt="Beto" class="cw-av-logo" />
            <template v-else>
              <img v-if="userAvatarUrl" :src="userAvatarUrl" :alt="authUser?.name" class="cw-av-photo" @error="(e) => (e.target as HTMLImageElement).style.display='none'" />
              <span v-else class="cw-av-ini">{{ userInitials }}</span>
            </template>
          </div>
          <div class="cw-col">
            <div class="cw-bub" :class="m.role" v-html="m.role === 'bot' ? md2h(m.text) : escHtml(m.text)"></div>
            <div class="cw-time">{{ m.time }}</div>
          </div>
        </div>

        <!-- Typing dots -->
        <div v-if="typing" class="cw-row bot">
          <div class="cw-av bot"><img :src="logoUrl" alt="Beto" class="cw-av-logo" /></div>
          <div class="cw-dots"><span></span><span></span><span></span></div>
        </div>
      </div>

      <!-- Input area -->
      <div class="cw-foot">
        <div class="cw-irow" :class="{ 'cw-irow--focus': focused }">
          <textarea
            ref="inputEl"
            v-model="text"
            rows="1"
            placeholder="Digite sua dúvida ou solicitação..."
            @keydown="onKey"
            @input="autoResize"
            @focus="focused = true"
            @blur="focused = false"
          ></textarea>
          <button class="cw-send" :disabled="busy || !text.trim()" @click="send">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
              <path d="M22 2L11 13M22 2L15 22L11 13L2 9L22 2Z"/>
            </svg>
          </button>
        </div>
        <div class="cw-hint">Assistente <strong>ISBET</strong> </div>
      </div>

    </div>
  </Transition>
</template>

<script setup lang="ts">
import { ref, computed, watch, nextTick, onMounted, onUnmounted } from 'vue'
import { useStore } from 'vuex'
import { avatarColor, initials } from '@/composables/useHelpers'
import { N8N_CHAT_WIDGET_URL } from '@/api/n8n'
import { useLogo } from '@/composables/useLogo'
import { activeConvContext } from '@/composables/useChatTrigger'
import { useAdmin } from '@/composables/useAdmin'

const EMPRESA_CLIENTE_FLUXO_ID = 2

const { logoUrl } = useLogo()
const WEBHOOK = N8N_CHAT_WIDGET_URL

const CHIPS = [
  { label: '📤 Novo aprendiz',  q: 'Quero cadastrar um novo aprendiz' },
  { label: '⚖️ Direitos CLT',   q: 'Quais são os direitos do jovem aprendiz pela CLT?' },
  { label: '📊 Frequência',     q: 'Gerar relatório de frequência do mês' },
  { label: '📍 Unidades SP',    q: 'Quais unidades do ISBET existem em SP?' },
]

interface Msg { role: 'bot' | 'user'; text: string; time: string }

const store   = useStore()
const authUser = computed(() => store.getters.authUser)
const userInitials  = computed(() => initials(authUser.value?.name || 'U'))
const userAvatarBg  = computed(() => avatarColor(authUser.value?.name || 'U'))
const userAvatarUrl = computed(() => authUser.value?.avatar_url || '')

const props = defineProps<{ sidebarCollapsed: boolean }>()

const fabVisible = ref(false)
const isOpen   = ref(false)
const messages = ref<Msg[]>([])
const typing   = ref(false)
const busy     = ref(false)
const text     = ref('')
const focused  = ref(false)
const unread   = ref(0)
const msgsEl   = ref<HTMLElement | null>(null)
const inputEl  = ref<HTMLTextAreaElement | null>(null)

const panelW = ref(355)
const panelH = ref(530)
const MIN_W = 280, MAX_W = 620
const MIN_H = 360, MAX_H = 780

const { globalSettings } = useAdmin()
const aiEnabled = computed(() => globalSettings.value.betoAiEnabled)

const fabLeft = computed(() => props.sidebarCollapsed ? '80px' : '236px')

// ── Consulta automática de CNPJ (fluxo 2 · Empresa Cliente) ──────────────
// Ver docs/planos/PILOTO-BETO-CNPJ.md: ao abrir uma conversa de Empresa Cliente com CNPJ
// preenchido, o Beto consulta sozinho em segundo plano (via MCP, do lado do n8n, painel fechado,
// sem indicador de digitação visível). Quando resolve, mostra o balão de fala com o nome da
// empresa encontrada — só ao clicar o agente vê o resumo completo no painel. Resultado (sucesso
// ou falha) fica cacheado por conversation_id nesta sessão do navegador, pra não repetir a
// consulta ao reabrir a mesma conversa.
// v3: cache passou a guardar { text, name } (JSON) em vez de só o texto — nome extraído da
// resposta pra personalizar o balão antes de abrir o painel.
// v4: timeout de 6s pra 25s (era curto demais pro AI Agent + MCP responderem, causava 'failed'
// falso-negativo) — muda o prefixo pra não manter presa uma tentativa que falhou só por timeout.
// v5: falha não fica mais presa pro resto da sessão — vira um "esfriamento" curto (1min); depois
// disso reabrir a conversa tenta de novo sozinho, em vez de exigir fechar a aba.
const CNPJ_CACHE_PREFIX = 'beto_cnpj_v5_'
const FAILURE_COOLDOWN_MS = 60_000
type ConsultCacheEntry =
  | { kind: 'result'; text: string; name: string | null }
  | { kind: 'failure'; at: number }
const autoConsultCache = ref<Record<number, string>>({})
const queryingConvIds = new Set<number>()
const pendingConvId = ref<number | null>(null)
let lastHandledConvId: number | null = null

function cnpjCacheKey(convId: number) { return CNPJ_CACHE_PREFIX + convId }

function getCachedConsult(convId: number): string | null {
  if (convId in autoConsultCache.value) return autoConsultCache.value[convId]
  const stored = sessionStorage.getItem(cnpjCacheKey(convId))
  if (stored !== null) autoConsultCache.value[convId] = stored
  return stored
}

function setCachedConsult(convId: number, value: string) {
  autoConsultCache.value[convId] = value
  sessionStorage.setItem(cnpjCacheKey(convId), value)
}

function decodeCacheEntry(raw: string): ConsultCacheEntry | null {
  try {
    const p = JSON.parse(raw)
    if (p?.kind === 'result' && typeof p.text === 'string') {
      return { kind: 'result', text: p.text, name: typeof p.name === 'string' ? p.name : null }
    }
    if (p?.kind === 'failure' && typeof p.at === 'number') return { kind: 'failure', at: p.at }
    return null
  } catch {
    return null
  }
}

function cachedEntryFor(convId: number): ConsultCacheEntry | null {
  const raw = getCachedConsult(convId)
  return raw ? decodeCacheEntry(raw) : null
}

const activeConvId     = computed(() => activeConvContext.value?.id ?? null)
const isEmpresaCliente = computed(() => activeConvContext.value?.fluxoId === EMPRESA_CLIENTE_FLUXO_ID)
const autoConsultResult = computed<{ text: string; name: string | null } | null>(() => {
  const id = activeConvId.value
  if (id == null) return null
  const entry = cachedEntryFor(id)
  return entry?.kind === 'result' ? { text: entry.text, name: entry.name } : null
})
const autoResolvedOk = computed(() => !!autoConsultResult.value)
const isConsultPending = computed(() => activeConvId.value != null && pendingConvId.value === activeConvId.value)

// Balão genérico (fallback): fluxo 2, consulta ainda não resolveu com sucesso, e não está em
// andamento agora — cobre "sem CNPJ", "ainda não tentada" (fora da janela silenciosa) e "falhou".
const helpHintActive = computed(() => aiEnabled.value && isEmpresaCliente.value && !autoResolvedOk.value && !isConsultPending.value)
// Balão personalizado: consulta já trouxe o nome da empresa, painel ainda fechado.
const nameHintActive = computed(() => aiEnabled.value && isEmpresaCliente.value && autoResolvedOk.value)
const nameHintText = computed(() => {
  const name = autoConsultResult.value?.name
  return name ? `Quer ajuda com ${name}?` : 'Encontrei os dados desse cliente — quer ajuda?'
})
const balloonActive = computed(() => helpHintActive.value || nameHintActive.value)
const balloonText   = computed(() => nameHintActive.value ? nameHintText.value : 'Precisa de Ajuda com a empresa? Pode falar comigo.')

// Ações rápidas do fluxo 2 (Empresa Cliente): fixas no topo do painel assim que a conversa tem
// CNPJ, oferecendo consultas específicas sobre esse mesmo CNPJ — não esperam o resumo automático
// resolver, então o agente pode usá-las mesmo antes ou se a consulta automática falhar.
interface ClienteAction { icon: string; label: string; prompt: string }
const clienteActions = computed<ClienteAction[]>(() => {
  const cnpj = activeConvContext.value?.cnpj
  if (!cnpj) return []
  const masked = fmtCnpjMask(cnpj)
  return [
    { icon: '💰', label: 'Débito Financeiro',      prompt: `Consulte o CNPJ ${masked} e me traga a situação de débito financeiro (valores em aberto, inadimplência, se houver) desse cliente.` },
    { icon: '🎓', label: 'Informações de Jovens',  prompt: `Consulte o CNPJ ${masked} e me traga as informações dos jovens aprendizes/estagiários vinculados a esse cliente (quantidade, nomes, status).` },
    { icon: '📇', label: 'Contatos',                prompt: `Consulte o CNPJ ${masked} e me traga os contatos (telefone, e-mail, responsável) desse cliente.` },
    { icon: '📄', label: 'Contratos',                prompt: `Consulte o CNPJ ${masked} e me traga os contratos vigentes desse cliente.` },
  ]
})

function fmtCnpjMask(cnpj: string): string {
  const d = cnpj.replace(/\D/g, '')
  return d.length === 14 ? `${d.slice(0, 2)}.${d.slice(2, 5)}.${d.slice(5, 8)}/${d.slice(8, 12)}-${d.slice(12)}` : cnpj
}

// Primeiro trecho em **negrito** da resposta — é onde o Beto já costuma destacar o nome da
// empresa (reforçado pela instrução abaixo). Sem parsing rígido: se não achar, o balão cai pro
// texto genérico em vez de quebrar.
function extractCompanyName(text: string): string | null {
  const m = text.match(/\*\*(.+?)\*\*/)
  return m ? m[1].trim() : null
}

// O workflow n8n hoje só lê `mensagem` como texto livre — por isso o pedido de consulta automática
// vai como uma instrução em português, igual a um agente teria digitado, em vez de um campo
// estruturado que o AI Agent ainda não sabe ler (ver docs/planos/PILOTO-BETO-CNPJ.md).
function buildAutoConsultPrompt(cnpj: string): string {
  return `Uma nova conversa de Empresa Cliente foi aberta. Consulte o CNPJ ${fmtCnpjMask(cnpj)} e comece a resposta destacando em **negrito** o nome da empresa. Depois traga um resumo básico (nome e o que encontrar) e pergunte se eu preciso de ajuda com esse cliente.`
}

async function autoQueryCnpj(convId: number, cnpj: string) {
  if (queryingConvIds.has(convId)) {
    console.info('[Beto] consulta automática já em andamento pra essa conversa, ignorando novo disparo', { convId })
    return
  }
  queryingConvIds.add(convId)
  pendingConvId.value = convId
  console.info('[Beto] disparando consulta automática de CNPJ', { convId, cnpj, webhook: WEBHOOK })

  try {
    const controller = new AbortController()
    // Painel fica fechado durante a espera (v3) — não há mais spinner visível pra evitar deixar
    // "pensando", então o timeout pode ser generoso o bastante pro AI Agent + ferramenta MCP
    // resolverem com folga, em vez dos 6s de quando isso abria o painel na hora.
    const timeoutId = setTimeout(() => controller.abort(), 25000)
    let res: Response
    try {
      res = await fetch(WEBHOOK, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        // mensagem carrega o pedido em texto — o mesmo campo que o envio manual já usa, então o
        // workflow n8n atual entende isso sem precisar de nenhum ajuste (ver docs/planos/PILOTO-BETO-CNPJ.md).
        // conversa vai junto como contexto extra, ignorável hoje, pra quando o workflow quiser lê-lo direto.
        body: JSON.stringify({ mensagem: buildAutoConsultPrompt(cnpj), conversa: { id: convId, fluxo_id: EMPRESA_CLIENTE_FLUXO_ID, cnpj } }),
        signal: controller.signal,
      })
    } finally {
      clearTimeout(timeoutId)
    }
    console.info('[Beto] resposta da consulta automática recebida', { convId, status: res.status, ok: res.ok })
    if (!res.ok) throw new Error(`HTTP ${res.status}`)
    const data = await res.json()
    const output =
      (Array.isArray(data) ? data[0]?.output  : data?.output)  ||
      (Array.isArray(data) ? data[0]?.text    : data?.text)    ||
      (Array.isArray(data) ? data[0]?.message : data?.message)
    if (!output) throw new Error(`resposta sem output/text/message reconhecível: ${JSON.stringify(data).slice(0, 300)}`)
    setCachedConsult(convId, JSON.stringify({ kind: 'result', text: output, name: extractCompanyName(output) } satisfies ConsultCacheEntry))
    console.info('[Beto] consulta automática de CNPJ concluída com sucesso', { convId, name: extractCompanyName(output) })
    // Painel continua fechado — a mensagem já entra na conversa (pro badge/histórico) e o balão
    // com o nome da empresa é quem convida o agente a abrir o painel e ver o resumo.
    addMsgOnce(output)
  } catch (err) {
    console.warn('[Beto] consulta automática de CNPJ falhou', { convId, err })
    setCachedConsult(convId, JSON.stringify({ kind: 'failure', at: Date.now() } satisfies ConsultCacheEntry))
  } finally {
    if (pendingConvId.value === convId) pendingConvId.value = null
    queryingConvIds.delete(convId)
  }
}

watch(activeConvContext, (ctx) => {
  const id = ctx?.id ?? null
  if (id === lastHandledConvId) return
  lastHandledConvId = id
  if (!ctx) return
  if (!aiEnabled.value) { console.info('[Beto] Beto desligado (betoAiEnabled) — não dispara consulta automática'); return }
  if (ctx.fluxoId !== EMPRESA_CLIENTE_FLUXO_ID) { console.info('[Beto] conversa não é fluxo 2 (Empresa Cliente) — não dispara', { fluxoId: ctx.fluxoId }); return }
  if (!ctx.cnpj) { console.info('[Beto] conversa fluxo 2 sem CNPJ em custom_attributes — não dispara', { convId: ctx.id }); return }

  // Sucesso já cacheado: o balão já reflete isso via computed (autoConsultResult), mas a mensagem
  // em si (messages) é só em memória — some num reload/remount mesmo com o sessionStorage intacto.
  // addMsgOnce garante repor sem duplicar se ela já estiver lá.
  // Falha cacheada: só não repete enquanto ainda estiver "esfriando" (FAILURE_COOLDOWN_MS); depois
  // disso, reabrir a conversa tenta de novo sozinho.
  const entry = cachedEntryFor(ctx.id)
  if (entry?.kind === 'result') {
    console.info('[Beto] já tinha resultado em cache pra essa conversa, repondo na lista de mensagens', { convId: ctx.id })
    addMsgOnce(entry.text)
    return
  }
  if (entry?.kind === 'failure' && Date.now() - entry.at < FAILURE_COOLDOWN_MS) {
    console.info('[Beto] falha recente em cooldown, não retenta ainda', { convId: ctx.id, faltamMs: FAILURE_COOLDOWN_MS - (Date.now() - entry.at) })
    return
  }
  autoQueryCnpj(ctx.id, ctx.cnpj)
}, { immediate: true })

const fabStyle   = computed(() => ({ left: fabLeft.value }))
const panelStyle = computed(() => ({
  left:   fabLeft.value,
  width:  panelW.value + 'px',
  height: panelH.value + 'px',
}))

let _rzDir: 'top' | 'right' | 'corner' | null = null
let _rzX = 0, _rzY = 0, _rzW = 0, _rzH = 0

function startResize(e: MouseEvent, dir: 'top' | 'right' | 'corner') {
  _rzDir = dir; _rzX = e.clientX; _rzY = e.clientY
  _rzW = panelW.value; _rzH = panelH.value
  document.addEventListener('mousemove', onResize)
  document.addEventListener('mouseup', stopResize)
}
function onResize(e: MouseEvent) {
  if (!_rzDir) return
  const dx = e.clientX - _rzX
  const dy = e.clientY - _rzY
  if (_rzDir === 'top' || _rzDir === 'corner')
    panelH.value = Math.min(MAX_H, Math.max(MIN_H, _rzH - dy))
  if (_rzDir === 'right' || _rzDir === 'corner')
    panelW.value = Math.min(MAX_W, Math.max(MIN_W, _rzW + dx))
}
function stopResize() {
  _rzDir = null
  document.removeEventListener('mousemove', onResize)
  document.removeEventListener('mouseup', stopResize)
}

function toggle() {
  if (!aiEnabled.value) return
  isOpen.value = !isOpen.value
  if (isOpen.value) {
    unread.value = 0
    nextTick(() => inputEl.value?.focus())
  }
}

watch(aiEnabled, (enabled) => {
  if (!enabled) { isOpen.value = false; fabVisible.value = false }
})

function ts() {
  return new Date().toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })
}

function scrollBottom() {
  nextTick(() => { if (msgsEl.value) msgsEl.value.scrollTop = msgsEl.value.scrollHeight })
}

function addMsg(role: 'bot' | 'user', txt: string) {
  messages.value.push({ role, text: txt, time: ts() })
  if (role === 'bot' && !isOpen.value) unread.value++
  scrollBottom()
}

// Usado pela consulta automática de CNPJ: o ciclo de auto-atualização da fila recalcula a conversa
// ativa periodicamente (às vezes passando por null antes de resolver de novo), o que pode disparar
// o mesmo resultado cacheado mais de uma vez. Em vez de tentar blindar cada re-disparo, garante que
// o mesmo texto nunca aparece duas vezes no histórico da sessão.
function addMsgOnce(txt: string) {
  if (messages.value.some(m => m.text === txt)) return
  addMsg('bot', txt)
}

async function send() {
  if (busy.value || !text.value.trim()) return
  const msg = text.value.trim()
  text.value = ''
  if (inputEl.value) inputEl.value.style.height = 'auto'
  busy.value = true

  addMsg('user', msg)
  typing.value = true
  scrollBottom()

  try {
    const res = await fetch(WEBHOOK, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ mensagem: msg }),
    })
    typing.value = false
    if (!res.ok) {
      addMsg('bot', `⚠️ Erro do servidor: ${res.status} ${res.statusText}`)
    } else {
      const data = await res.json()
      const output =
        (Array.isArray(data) ? data[0]?.output  : data?.output)  ||
        (Array.isArray(data) ? data[0]?.text    : data?.text)    ||
        (Array.isArray(data) ? data[0]?.message : data?.message) ||
        JSON.stringify(data)
      addMsg('bot', output)
    }
  } catch {
    typing.value = false
    addMsg('bot', '⚠️ Não foi possível conectar ao servidor. Verifique se o n8n está rodando.')
  }

  busy.value = false
  nextTick(() => inputEl.value?.focus())
}

function quickSend(q: string) { text.value = q; send() }
function onKey(e: KeyboardEvent) { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); send() } }
function autoResize(e: Event) {
  const el = e.target as HTMLTextAreaElement
  el.style.height = 'auto'
  el.style.height = Math.min(el.scrollHeight, 100) + 'px'
}

function handleShortcut(e: KeyboardEvent) {
  if (e.ctrlKey && e.shiftKey && (e.key === '*' || e.key === '8')) {
    e.preventDefault()
    if (!aiEnabled.value) return
    fabVisible.value = !fabVisible.value
    if (!fabVisible.value) isOpen.value = false
  }
}

function escHtml(s: string) {
  return s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
}

function md2h(s: string) {
  return s
    .replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>')
    .replace(/\*(.+?)\*/g, '<em>$1</em>')
    .replace(/`(.+?)`/g, '<code>$1</code>')
    .replace(/^---$/gm, '<hr>')
    .replace(/^#{1,3} (.+)$/gm, '<strong>$1</strong>')
    .replace(/^- (.+)$/gm, '<li>$1</li>')
    .replace(/^\d+\. (.+)$/gm, '<li>$1</li>')
    .replace(/(<li>[\s\S]*?<\/li>\n?)+/g, m => `<ul>${m}</ul>`)
    .replace(/\n\n/g, '<br><br>')
    .replace(/\n/g, '<br>')
}

onMounted(() => window.addEventListener('keydown', handleShortcut))
onUnmounted(() => {
  window.removeEventListener('keydown', handleShortcut)
  stopResize()
})
</script>

<style scoped>
/* ── FAB ──────────────────────────────────────────── */
.cw-fab {
  position: fixed;
  bottom: 24px;
  width: 50px;
  height: 50px;
  border-radius: 50%;
  background: var(--night);
  border: 2px solid rgba(255, 255, 255, 0.12);
  color: #fff;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1200;
  box-shadow: 0 4px 22px rgba(2, 22, 39, 0.4);
  transition: left 0.26s cubic-bezier(0.4, 0, 0.2, 1), background 0.18s, transform 0.18s;
}
.cw-fab svg { width: 22px; height: 22px; }
.cw-fab:not(.cw-fab--open) { animation: cw-pulse 3.5s ease-in-out infinite; }
.cw-fab:hover { background: var(--red); transform: scale(1.1); }
.cw-fab--open { background: var(--red); animation: none !important; }

@keyframes cw-pulse {
  0%, 100% { box-shadow: 0 4px 22px rgba(2, 22, 39, 0.4); }
  50%       { box-shadow: 0 4px 28px rgba(188, 12, 36, 0.55), 0 0 0 6px rgba(188, 12, 36, 0.08); }
}

.cw-fab--hint { animation: cw-pulse 1.6s ease-in-out infinite; }

/* ── Balão de sugestão ──────────────────────────── */
.cw-hint-balloon {
  position: fixed;
  bottom: 82px;
  max-width: 210px;
  background: var(--night);
  color: #fff;
  padding: 10px 14px;
  border-radius: 14px;
  border-bottom-left-radius: 4px;
  font-size: 12px;
  font-weight: 600;
  line-height: 1.45;
  box-shadow: 0 8px 26px rgba(2, 22, 39, 0.32);
  cursor: pointer;
  z-index: 1150;
  transition: left 0.26s cubic-bezier(0.4, 0, 0.2, 1);
}
.cw-hint-balloon::after {
  content: '';
  position: absolute;
  bottom: -7px; left: 14px;
  width: 12px; height: 12px;
  background: var(--night);
  clip-path: polygon(0 0, 100% 0, 0 100%);
}
.cw-balloon-enter-active { transition: opacity 0.22s ease, transform 0.22s cubic-bezier(0.34, 1.56, 0.64, 1); }
.cw-balloon-leave-active { transition: opacity 0.14s ease, transform 0.14s ease; }
.cw-balloon-enter-from,
.cw-balloon-leave-to { opacity: 0; transform: translateY(8px) scale(0.94); }

.cw-fab-badge {
  position: absolute;
  top: -4px; right: -4px;
  min-width: 18px; height: 18px;
  background: var(--orange);
  color: #fff;
  border-radius: 9px;
  font-size: 10px; font-weight: 700;
  display: flex; align-items: center; justify-content: center;
  padding: 0 4px;
  border: 2px solid #fff;
  animation: cw-pop 0.3s cubic-bezier(0.34, 1.56, 0.64, 1);
}
@keyframes cw-pop { from { transform: scale(0); } to { transform: scale(1); } }

/* ── Panel ──────────────────────────────────────── */
.cw-panel {
  position: fixed;
  bottom: 86px;
  background: #fff;
  border-radius: 18px;
  box-shadow: 0 12px 48px rgba(2, 22, 39, 0.28);
  display: flex;
  flex-direction: column;
  overflow: hidden;
  z-index: 1100;
  border: 1px solid var(--ink-10);
  transition: left 0.26s cubic-bezier(0.4, 0, 0.2, 1);
  min-width: 280px;
  min-height: 360px;
}

/* ── Resize handles ─────────────────────────────── */
.cw-rz { position: absolute; z-index: 10; }
.cw-rz-top {
  top: 0; left: 12px; right: 12px; height: 6px;
  cursor: n-resize;
}
.cw-rz-right {
  top: 12px; right: 0; bottom: 12px; width: 6px;
  cursor: e-resize;
}
.cw-rz-corner {
  top: 0; right: 0; width: 22px; height: 22px;
  cursor: ne-resize;
  border-top-right-radius: 18px;
  display: flex; align-items: center; justify-content: center;
  color: rgba(255, 255, 255, 0.4);
  transition: color 0.15s;
}
.cw-rz-corner:hover { color: rgba(255, 255, 255, 0.85); }
.cw-rz-corner svg { width: 10px; height: 10px; pointer-events: none; }

/* ── Transition ─────────────────────────────────── */
.cw-enter-active { transition: opacity 0.28s ease, transform 0.28s cubic-bezier(0.34, 1.56, 0.64, 1); }
.cw-leave-active { transition: opacity 0.16s ease, transform 0.16s cubic-bezier(0.4, 0, 1, 1); }
.cw-enter-from   { opacity: 0; transform: translateY(18px) scale(0.92); }
.cw-leave-to     { opacity: 0; transform: translateY(12px) scale(0.95); }

/* ── Header ─────────────────────────────────────── */
.cw-header {
  background: var(--night);
  padding: 0 16px;
  height: 56px;
  display: flex; align-items: center; justify-content: space-between;
  flex-shrink: 0;
}
.cw-hleft { display: flex; align-items: center; gap: 10px; }
.cw-logo {
  font-family: 'Poppins', sans-serif;
  font-weight: 800; font-size: 14px;
  color: #fff; letter-spacing: -0.3px;
}
.cw-logo b { color: var(--orange); font-style: normal; }
.cw-logo-img { height: 32px; object-fit: contain; display: block; }
.cw-vline { width: 1px; height: 22px; background: rgba(255, 255, 255, 0.18); }
.cw-htitle { font-size: 12px; font-weight: 600; color: #fff; }
.cw-hsub   { font-size: 10px; color: rgba(255, 255, 255, 0.5); margin-top: 1px; }
.cw-hstatus {
  display: flex; align-items: center; gap: 5px;
  font-size: 10.5px; font-weight: 600;
  color: rgba(255, 255, 255, 0.7);
}
.cw-dot {
  width: 6px; height: 6px; background: #4ade80; border-radius: 50%;
  animation: cw-blink 2s ease-in-out infinite;
}
@keyframes cw-blink { 0%, 100% { opacity: 1; } 50% { opacity: 0.3; } }

.cw-stripe {
  height: 3px;
  background: linear-gradient(90deg, var(--red) 0%, var(--orange) 65%, transparent 100%);
  flex-shrink: 0;
}

/* ── Chips ──────────────────────────────────────── */
.cw-chips {
  display: flex; gap: 5px;
  padding: 8px 10px 0;
  overflow-x: auto; flex-shrink: 0;
  scrollbar-width: none;
}
.cw-chips::-webkit-scrollbar { display: none; }

/* Ações do fluxo 2, fixas entre o header e a lista de mensagens (fora de .cw-welcome, então
   precisa do próprio padding lateral/inferior) */
.cw-qgrid--top { padding: 10px 12px 6px; flex-shrink: 0; border-bottom: 1px solid var(--ink-05); }
.cw-chip {
  flex-shrink: 0;
  padding: 4px 10px;
  border-radius: 16px;
  font-size: 11px; font-weight: 600;
  cursor: pointer; white-space: nowrap;
  border: 1.5px solid var(--ink-10);
  color: var(--ink-60);
  background: #fff;
  font-family: 'Poppins', sans-serif;
  transition: all 0.14s;
}
.cw-chip:hover { background: var(--night); color: #fff; border-color: var(--night); }

/* ── Messages area ──────────────────────────────── */
.cw-msgs {
  flex: 1; overflow-y: auto;
  padding: 12px 12px 8px;
  display: flex; flex-direction: column; gap: 10px;
  scroll-behavior: smooth;
}
.cw-msgs::-webkit-scrollbar { width: 3px; }
.cw-msgs::-webkit-scrollbar-thumb { background: var(--ink-10); border-radius: 3px; }

/* ── Welcome ────────────────────────────────────── */
.cw-welcome {
  display: flex; flex-direction: column; align-items: center;
  text-align: center; gap: 7px;
  padding: 12px 8px 4px;
  animation: cw-up 0.3s ease;
}
.cw-w-icon {
  width: 44px; height: 44px;
  background: rgba(188, 12, 36, 0.08);
  border-radius: 12px;
  display: flex; align-items: center; justify-content: center;
  color: var(--red);
}
.cw-w-icon svg { width: 24px; height: 24px; }
.cw-welcome h3 { font-size: 14px; font-weight: 700; color: var(--night); }
.cw-welcome p  { font-size: 11.5px; color: var(--ink-40); line-height: 1.6; max-width: 270px; }

.cw-qgrid {
  display: grid; grid-template-columns: 1fr 1fr;
  gap: 6px; width: 100%; margin-top: 4px;
}
.cw-qbtn {
  padding: 9px 8px;
  background: #fff; border: 1.5px solid var(--ink-10);
  border-radius: 10px;
  font-size: 11px; font-weight: 600; color: var(--ink-60);
  cursor: pointer; text-align: left;
  font-family: 'Poppins', sans-serif; line-height: 1.4;
  box-shadow: var(--shadow-sm);
  transition: all 0.14s;
  display: flex; flex-direction: column; gap: 3px;
}
.cw-qi  { font-size: 17px; }
.cw-qbtn:hover { border-color: var(--red); background: rgba(188, 12, 36, 0.04); color: var(--red); transform: translateY(-2px); box-shadow: var(--shadow-md); }

/* ── Message rows ───────────────────────────────── */
.cw-row { display: flex; gap: 7px; align-items: flex-end; animation: cw-up 0.2s ease; }
.cw-row.user { flex-direction: row-reverse; }
@keyframes cw-up { from { opacity: 0; transform: translateY(6px); } to { opacity: 1; transform: translateY(0); } }

.cw-av {
  width: 30px; height: 30px; border-radius: 50%;
  display: flex; align-items: center; justify-content: center;
  font-size: 9px; font-weight: 800; flex-shrink: 0;
  overflow: hidden;
}
.cw-av.bot  { background: var(--night); padding: 5px; }
.cw-av-logo { width: 100%; height: 100%; object-fit: contain; display: block; }
.cw-av.user { border: 1.5px solid rgba(255,255,255,.25); }
.cw-av-photo {
  width: 100%; height: 100%; object-fit: cover;
  border-radius: 50%; display: block;
}
.cw-av-ini {
  font-size: 10px; font-weight: 700;
  color: #fff; font-family: 'Poppins', sans-serif;
  text-transform: uppercase; line-height: 1;
}

.cw-col { display: flex; flex-direction: column; gap: 2px; max-width: 82%; }
.cw-row.user .cw-col { align-items: flex-end; }

.cw-bub {
  padding: 9px 12px; border-radius: 12px;
  font-size: 12.5px; line-height: 1.65;
  box-shadow: var(--shadow-sm);
}
.cw-bub.bot  {
  background: #fff; color: var(--ink-80);
  border: 1px solid var(--ink-10); border-bottom-left-radius: 3px;
}
.cw-bub.user { background: var(--night); color: #fff; border-bottom-right-radius: 3px; }
.cw-bub.bot :deep(strong) { font-weight: 700; color: var(--night); }
.cw-bub.bot :deep(ul)     { padding-left: 16px; margin-top: 4px; }
.cw-bub.bot :deep(li)     { margin-bottom: 2px; }
.cw-bub.bot :deep(code)   { font-size: 11px; background: var(--ink-05); padding: 1px 4px; border-radius: 3px; color: var(--red); }
.cw-bub.bot :deep(hr)     { border: none; border-top: 1px solid var(--ink-10); margin: 6px 0; }

.cw-time { font-size: 9.5px; color: var(--ink-40); }

/* ── Typing dots ────────────────────────────────── */
.cw-dots {
  background: #fff; border: 1px solid var(--ink-10);
  border-radius: 12px; border-bottom-left-radius: 3px;
  padding: 10px 14px; display: flex; gap: 4px; align-items: center;
  box-shadow: var(--shadow-sm);
}
.cw-dots span {
  width: 5px; height: 5px; background: var(--ink-40); border-radius: 50%;
  animation: cw-td 1.2s ease-in-out infinite;
}
.cw-dots span:nth-child(2) { animation-delay: 0.18s; }
.cw-dots span:nth-child(3) { animation-delay: 0.36s; }
@keyframes cw-td { 0%, 80%, 100% { transform: translateY(0); opacity: 0.4; } 40% { transform: translateY(-5px); opacity: 1; } }

/* ── Input / footer ─────────────────────────────── */
.cw-foot {
  background: #fff; border-top: 1px solid var(--ink-10);
  padding: 10px 12px; flex-shrink: 0;
}
.cw-irow {
  display: flex; gap: 7px; align-items: flex-end;
  background: var(--ink-05); border: 1.5px solid var(--ink-10);
  border-radius: 12px; padding: 7px 7px 7px 12px;
  transition: border-color 0.15s, box-shadow 0.15s;
}
.cw-irow--focus { border-color: var(--night); background: #fff; box-shadow: 0 0 0 3px rgba(2, 22, 39, 0.06); }
.cw-irow textarea {
  flex: 1; border: none; background: transparent;
  font-family: 'Poppins', sans-serif; font-size: 12.5px; color: var(--ink-80);
  resize: none; outline: none; max-height: 100px; line-height: 1.5;
}
.cw-irow textarea::placeholder { color: var(--ink-40); }
.cw-send {
  width: 32px; height: 32px; background: var(--red); border: none;
  border-radius: 8px; color: #fff; cursor: pointer; flex-shrink: 0;
  display: flex; align-items: center; justify-content: center;
  transition: background 0.14s, transform 0.14s;
}
.cw-send:hover    { background: var(--red-dark); transform: scale(1.08); }
.cw-send:disabled { background: var(--ink-10); cursor: not-allowed; transform: none; }
.cw-send svg { width: 13px; height: 13px; }
.cw-hint { text-align: center; font-size: 9.5px; color: var(--ink-40); margin-top: 5px; }
.cw-hint strong { color: var(--night); font-weight: 700; }
</style>
